import { valita } from '@app/utils/valita'

import type { ApiConsentStatus, ApiConsentTypeKey, ApiDocumentDocumentType, TrainingFormatName } from './entities'
import type { ApiRequestStatus, Citizenship, ISO8601, KidsDescriptor, ReviewRequestQA, UNIXTimestamp } from './misc'

export type FaqFilter = {
  'filter[for_playground]'?: 'true' | 'false'
  'filter[for_knowledgebase]'?: 'true' | 'false'
  'filter[for_sitters]'?: 'true' | 'false'
  'filter[for_parents]'?: 'true' | 'false'
  'filter[for_landing]'?: 'true' | 'false'
}

export type GetApiPolicyQuery = {
  'filter[key]'?: string
  'filter[consent_type_key]'?: ApiConsentTypeKey[]
  'filter[effective_at_gt]'?: string
}

export type GetUserConsentsQuery = {
  'filter[consent_type_key]'?: ApiConsentTypeKey[]
  'filter[policy_id]'?: string
}

export type PostUserConsentsBulkRequest = { user_consents: UserConsentRequestItem[] }

export type UserConsentRequestItem = {
  consent_type_key: ApiConsentTypeKey
  policy_id: string
  status: ApiConsentStatus
  source: string
  consent_method: string
  consent_text: string
}

export type ComplaintRequest = { subject_type: 'users' | 'announcements'; subject_id: string; message: string }

export type GetStatisticsQuery = { cache: string }

export type GetAnnouncementsSearchStatsQuery = {
  require_attention?: boolean
  /** e.g "08:00" */
  start?: string
  /** e.g "12:00" */
  end?: string
  min_kids?: number
  max_kids?: number
  youngest_age_months?: number
  eldest_age_months?: number
  regular?: boolean
  online?: boolean
  created_by?: 'all' | 'favorites' | 'except_ignored'
  latitude?: number
  longitude?: number
  /** radius in kilometers */
  distance?: number
}

export type TrackingEvent =
  | { type: 'signup'; data: { user_id: number } }
  | { type: 'subscription'; data: { user_id: number; subscription_id: number } }
  | { type: 'school_request'; data: { user_id: number; school_request_id: number } }

export type GetAccountIssuesQuery = {
  'filter[status]'?: string
}

export type PostChargesCloudpaymentsRequest = { invoice_tracking_id: string; crypto: string; holder_name: string }

export type PostChargesCloudpaymentsConfirmRequest = { pa_res: string; transaction_id: string }

export type PostSchoolRequestsRequest = {
  course: 'sitter' | 'parent' | 'nanny'
  provider?: 'vkontakte' | 'facebook'
  token: string
  first_name: string
  last_name: string
  email: string
  city_id: number
  age: number
  money: number
  phone: string
  hours?: number
  job?: string
  purpose?: string
  knowledge_source: string
}

export type PostPlaygroundRequestsRequest = {
  name: string
  phone: string
  /** YYYY-MM-DD */
  date: string
  email?: string
  start_time?: string
  end_time?: string
  type_id?: number
  number_of_infants?: number
  number_of_kids?: number
  number_of_teenagers?: number
  comments?: string
  toys?: boolean
  creativity_materials?: boolean
  sport_equipment?: boolean
  play_structures?: boolean
  master_classes?: boolean
  shows?: boolean
  decors?: boolean
}

export type PostTelegramBotConnectionsRequest = {
  bot: string
  auth_data: {
    id: number
    first_name: string
    last_name?: string
    username?: string
    photo_url?: string
    auth_date: number
    hash: string
  }
}

export type ApiAppStoreCustomerReviewRequest = {
  'filter[rating]'?: string[]
  /** example: ru.kidsout.parent */
  'filter[bundle_id]'?: string
  sort?: '-created_at' | 'created_at'
  page?: number
  per_page?: number
}

export type TBankReviewRequest = {
  'filter[rating]'?: string[]
  sort?: '-created_at' | 'created_at'
  page?: number
  per_page?: number
}

export type YandexMapsReviewRequest = {
  'filter[rating]'?: string[]
  sort?: '-created_at' | 'created_at'
  page?: number
  per_page?: number
}

/** The paging block the three review feeds return. */
export type ReviewsPageMeta = { total: number; total_pages: number; current_page: number }

export type PostDocumentsRequest = {
  type: ApiDocumentDocumentType
  file: File
}

export type ApiPersonnelCheckOrderRequest = {
  email: string
  type_id: string
}

export type NewsMeta = {
  total: number
  total_pages: number
  current_page: number
}

export type AvailableTimesPutRequestItem = {
  dtstart: string
  dtend: string
  rrule?: string
}

export type AvailableTimesPutRequest = { records: AvailableTimesPutRequestItem[] }

export const RT_SitterSearchSort = valita.union(
  valita.literal('-kidsout_score'),
  valita.literal('rate'),
  valita.literal('-rate'),
  valita.literal('age'),
  valita.literal('-age'),
  valita.literal('distance'),
  valita.literal('experience'),
  valita.literal('-experience'),
  valita.literal('-return_rate')
)
export type SitterSearchSort = valita.Infer<typeof RT_SitterSearchSort>

export type SitterSearchQuery = {
  online?: boolean
  /** format: 20190418 */
  date?: string
  /** format: 00:30, must be sent with start or not sent */
  end?: string
  /** format: 00:30, must be sent with end or not sent */
  start?: string
  special_needs?: boolean
  kidsout_school?: boolean
  babies?: boolean
  /** number of kids as string */
  kids?: string
  /** array of perk id */
  perks?: string[]
  /** min rate per hour for 1 child */
  min_rate?: number
  /** max rate per hour for 1 child */
  max_rate?: number
  rate_currency?: string

  /** example: 42.2123123 */
  latitude?: number
  /** example: 42.2123123 */
  longitude?: number
  distance?: number

  'bbox[bl][lat]'?: number
  'bbox[bl][lon]'?: number
  'bbox[tr][lat]'?: number
  'bbox[tr][lon]'?: number

  page?: number
  per_page?: number

  sort?: SitterSearchSort
}

export type SitterAvailabilityCheckRequest = {
  /** format 2019-04-23; sent instead of the legacy `date`, which cannot say an order ends days later */
  start_date: string
  /** format 2019-04-23 */
  end_date: string
  start: string
  end: string
  latitude?: number
  longitude?: number
  online?: boolean
}

export type ProfileUpdateRequest = {
  /** [Registration only] account_type */
  account_type?: 'sitter' | 'parent'
  /** First name */
  first_name?: string
  /** Last name */
  last_name?: string
  /** Email */
  email?: string
  /** [Registration only] Send either place_id or region_id on registration */
  place_id?: string
  /** [Registration only] Send either place_id or region_id on registration */
  region_id?: string
  /** Gender */
  gender?: 'male' | 'female'
  /** Citizenship */
  citizenship?: Citizenship | null
  /** [Parents only] Payment method */
  payment_method?: 'subscription' | 'commission' | null
  /** [Parents only] Preferred sitter description */
  about_sitter?: string
  /** [Sitters only] Date of birth */
  birthday?: string
  /** [Sitters only] Short text about sitter */
  /** @deprecated [Sitters only] Short text about sitter */
  about?: string
  /** [Sitters only] */
  education_and_experience?: string
  /** [Sitters only] */
  motivation?: string
  /** [Sitters only] */
  late_booking_transport_details?: string
  /** [Sitters only] */
  late_arrival_policy?: string
  /** [Sitters only] */
  additional_info?: string
  /** [Sitters only] Sitter's rate currency */
  rate_currency?: string
  /** [Sitters only] Sitter's rate per hour */
  rate?: string
  /** [Sitters only] Sitter's rate per hour for 2 children */
  rate_for_2_kids?: string
  /** [Sitters only] Sitter's rate per hour for 3 children */
  rate_for_3_kids?: string
  /** [Sitters only] Sitter's rate per hour for 4 children */
  rate_for_4_kids?: string
  /** [Sitters only] Sitter's rate per hour for 5 children */
  rate_for_5_kids?: string
  /** [Sitters only] Sitter's rate per hour for online */
  rate_online?: string
  /** [Sitters only] Sitter's minimal order price */
  minimum_request_amount?: string | null
  /** [Sitters only] Is sitter agree to sit with babies (0-2 years old) */
  babies?: boolean
  /** [Sitters only] Is sitter agree to meet with parents for free */
  free_meeting?: boolean
  /** [Sitters only] Maximum babies sitter is agree to be with */
  max_children?: number
  /** [Sitters only] Attitude towards animals */
  animals?: string
  /** [Sitters only] Whether sitter is on vacation or not */
  absent?: boolean
  /** [Sitters only] Whether sitter is ready for online work */
  work_online?: boolean
  /** [Sitters only] Whether sitter is visible on search today or not */
  work_today?: boolean
  /** [Sitters only] Sitter agreed to come to children with special needs */
  special_needs?: boolean
  /** [Sitters only] Sitter is agree to accept requests made in evening on morning */
  accept_night_requests?: boolean
  /** [Sitters only] Sitter is agree to accept urgent requests */
  accept_urgent_requests?: boolean
  /** [Sitters only] Sitter accepts payments on card */
  card_payment_possible?: boolean
  /** [Sitters only] Facebook profile URL */
  fb_url?: string
  /** [Sitters only] Vkonatakte profile URL */
  vk_url?: string
  /** [Sitters only] Address */
  address?: string
  /** [Sitters only] Address latitude */
  latitude?: number
  /** [Sitters only] Address longitude */
  longitude?: number
  /** format: "Europe/Moscow" */
  timezone?: string
  /** format: 10800 (3h * 60m * 60s) (+3 hours) */
  utc_offset?: number
  /** [Sitters only] */
  who_can_invite?: 'all' | 'favorites' | 'nobody' | 'except_ignored'
  /* [Non Visitors only] Currency iso_code */
  display_currency?: string
}

export type DepersonalizedAnnouncementQueryRequest = {
  'filter[id]'?: string[]
}

export type AnnouncementSearchQuery = {
  online?: boolean
  require_attention?: boolean
  date?: string
  min_date?: string
  max_date?: string
  min_hours?: number
  max_hours?: number
  start?: string
  end?: string
  min_kids?: number
  max_kids?: number
  youngest_age_months?: number
  eldest_age_months?: number
  regular?: boolean

  latitude?: number
  longitude?: number
  'bbox[bl][lat]'?: number
  'bbox[bl][lon]'?: number
  'bbox[tr][lat]'?: number
  'bbox[tr][lon]'?: number
  distance?: number

  order_by?: 'create_time' | 'start_time' | 'duration' | 'distance'
  order_dir?: 'asc' | 'desc'

  created_after?: string
  page: number
  per_page?: number
  timezone: string

  created_by?: 'all' | 'favorites' | 'except_ignored'
}

export type GetReviewsQuery = {
  page?: number
  per_page: number
  user_id?: string
  token?: string
  before?: UNIXTimestamp
}

export type GetReviewsPublicFromParentsQuery = {
  page: number
  per_page: number
  'filter[length_gteq]'?: number
  'filter[length_lteq]'?: number
}

export type RequestReviewCreateRequest = {
  request_id: string
  rate: 0 | 1 | -1
  body: string
}

export type RequestReviewQACreateRequest = {
  request_id: string
  qa: ReviewRequestQA
}

export type TrainingSignUpRequest = {
  /** Training course */
  course: 'sitter' | 'nanny'
  /** Training format */
  format?: TrainingFormatName
  /** Purpose of becoming a sitter */
  purpose: string
  /** Work or study place */
  job: string
  /** Working experience with children */
  experience: string
  /** Link to social network account */
  social_network_link: string
  /** Youngest child age */
  youngest_child_age?: string
  /** promocode */
  promocode?: string
  knowledge_source: string
  passport_check_agreement: boolean
  psychologist_check_agreement: boolean
  preferred_contact_method?: 'phone' | 'email' | 'whatsapp'
}

export type PostAnnouncementResponsesRequest = {
  announcement_id: string
  price: string
  currency: string
}

export type PutAnnouncementResponsesRequest = {
  price: string
  currency: string
}

export type SavedAnnouncementSearchesByIdAnnouncemenetsQuery = { page: number; per_page?: number } | { cursor: string }

export type SavedAnnouncementSearchPostRequest = {
  name: string // e.g "Morning Babysitting"
  min_date?: string | null // e.g "2023-10-15"
  max_date?: string | null // e.g "2023-10-15"
  min_hours: number | null
  max_hours: number | null
  start?: string | null // e.g "08:00",
  end?: string | null // e.g "12:00",
  min_kids?: number | null
  max_kids?: number | null
  youngest_age_months?: number | null
  eldest_age_months?: number | null
  regular?: boolean | null
  online?: boolean | null
  created_by?: 'all' | 'favorites' | 'except_ignored'
  address?: string | null
  latitude?: number | null
  longitude?: number | null
  /** radius in kilometers */
  distance?: number | null
}

export type SavedAnnouncementSearchPutRequest = {
  name?: string // e.g "Morning Babysitting"
  min_date?: string | null // e.g "2023-10-15"
  max_date?: string | null // e.g "2023-10-15"
  min_hours: number | null
  max_hours: number | null
  start?: string | null // e.g "08:00",
  end?: string | null // e.g "12:00",
  min_kids?: number | null
  max_kids?: number | null
  youngest_age_months?: number | null
  eldest_age_months?: number | null
  regular?: boolean | null
  online?: boolean | null
  created_by?: 'all' | 'favorites' | 'except_ignored'
  address?: string | null
  latitude?: number | null
  longitude?: number | null
  /** radius in kilometers */
  distance?: number | null
}

export type ApiDirectAnnouncementPreviewRequest = {
  user_id: string
  /** format 2019-04-23; sent instead of the legacy `date`, which cannot say an order ends days later */
  start_date: string
  /** format 2019-04-23 */
  end_date: string
  start: string
  end: string
  kids_data: KidsDescriptor[]
  sick_child?: boolean
  special_needs?: boolean
  online?: boolean
  address?: string
  latitude?: number
  longitude?: number
  address_details?: string
  allow_date_jump?: boolean
}

export type DirectAnnouncementCalculateRequest = {
  user_id: string
  /** format 2019-04-23; sent instead of the legacy `date`, which cannot say an order ends days later */
  start_date: string
  /** format 2019-04-23 */
  end_date: string
  start: string
  end: string
  latitude: number
  longitude: number
  kids_data: KidsDescriptor[]
  allow_date_jump: boolean
}

export type DirectAnnouncementRequest = {
  user_id: string
  price: string
  currency: string
  /** format: 2019-08-02 */
  /** format 2019-04-23; sent instead of the legacy `date`, which cannot say an order ends days later */
  start_date: string
  /** format 2019-04-23 */
  end_date: string
  /** format: 08:30 */
  start: string
  /** format: 08:30 */
  end: string
  address: string
  latitude: number
  longitude: number
  description: string
  children: KidsDescriptor[]
  special_needs: boolean
  sick_child: boolean
  allow_date_jump: boolean
  online: boolean
}

export type PutDirectAnnouncementsAcceptRequest = {
  price: string
  currency: string
}

export type AcademyCartPurchaseRequest = {
  session_id: string
  items: { lesson_id: string }[]
  email: string
  phone: string
  promocode?: string
}

export const RT_ACADEMY_INVOICE_CREATE_REQUEST = valita.object({
  email: valita.string(),
  phone: valita.string(),
  name: valita.string(),
  session_id: valita.string(),
  items: valita.array(
    valita.object({
      lesson_id: valita.string(),
      name: valita.string(),
      age: valita.number(),
    })
  ),
  promocode: valita.string().optional(),
})

export type AcademyInvoiceCreateRequest = valita.Infer<typeof RT_ACADEMY_INVOICE_CREATE_REQUEST>

export type AnnouncementCreateRequest = {
  /** format 2019-04-23; sent instead of the legacy `date`, which cannot say an order ends days later */
  start_date: string
  /** format 2019-04-23 */
  end_date: string
  start: string
  end: string
  children: KidsDescriptor[]
  address: string
  latitude: number
  longitude: number

  school?: boolean
  special_needs?: boolean
  description: string
  price?: string | null
  currency?: string
  price_policy?: 'flexible' | 'maximum'
  /** Denotes that sitter will be marked as trusted after request completed */
  regular?: boolean

  /** Denotes that sitter will be working with ill child */
  sick_child?: boolean

  /** Denotes that request will be created immediately after first response to announcement */
  auto_accept?: boolean

  /** Denotes that request will be executed online (via skype, telegram, etc) */
  online?: boolean
  allow_date_jump?: boolean

  /** Defines that announcement can be indexed and searched by external services */
  external_publication_consent?: boolean
}

export type AnnouncementQueryRequest = {
  'filter[id]'?: string[]
  'filter[parent_id]'?: string[]
}

export type GetRequestsQuery = {
  per_page?: number
  page: number
  sort?: 'start_time' | '-start_time'
  'filter[start_time_gt]'?: ISO8601
  'filter[start_time_lt]'?: ISO8601
  'filter[end_time_gt]'?: ISO8601
  'filter[end_time_lt]'?: ISO8601
  'filter[status_in]'?: ApiRequestStatus[]
  'filter[status_not_in]'?: ApiRequestStatus[]
}

export type RequestRejectRequest = {
  reason: string
  replacement_provided?: boolean
  parent_notified?: boolean
  details?: string
}

export type TrustedOrderRequest = {
  price: string
  currency: string
  /** format: 2019-08-02 */
  /** format 2019-04-23; sent instead of the legacy `date`, which cannot say an order ends days later */
  start_date: string
  /** format 2019-04-23 */
  end_date: string
  /** format: 08:30 */
  start: string
  /** format: 08:30 */
  end: string

  address: string
  latitude: number
  longitude: number

  description: string
  online: boolean
  children: KidsDescriptor[]
  allow_date_jump: boolean
}

export type RequestCancelRequest = {
  reason: string
}

export type PostTrackingsRequest = {
  /** Stable per event and reused by every retry — the server dedupes on it. */
  event_id: string
  event_type: string
  event_data: Record<string, any>
  event_ts?: number
  referrer?: string
}
